import React, { useState, useRef, useLayoutEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
} from "framer-motion";
import { ParallaxProps } from "../common/types";

const Parallax: React.FC<ParallaxProps> = ({
  children,
  offset = 50,
  borderRadius,
  $className,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const [elementTop, setElementTop] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  //How far we've scrolled
  const { scrollY } = useScroll();

  // start animating our element when we've scrolled it into view
  const initial = elementTop - clientHeight;
  // end our animation when we've scrolled the offset specified
  const final = elementTop + offset;

  const yRange = useTransform(scrollY, [initial, final], [offset, -offset]);

  //Round the yRange value to avoid sub-pixel rendering issues
  const roundedYRange = useTransform(yRange, (value) => Math.round(value));

  //Adding spring easing with useSpring
  const y = useSpring(roundedYRange, { stiffness: 400, damping: 100 });

  useLayoutEffect(() => {
    const element = ref.current;
    // save our layout measurements in a function in order to trigger
    // it both on mount and on resize
    const onResize = () => {
      if (element) {
        setElementTop(element.getBoundingClientRect().top + window.scrollY);
        setClientHeight(window.innerHeight);
      }
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [ref]);

  //Parallax accessibility: useReducedMotion
  if (prefersReducedMotion) {
    return <>{children}</>;
  }

  return (
    <motion.div
      ref={ref}
      style={{
        y,
        borderRadius: borderRadius,
        willChange: "transform",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        // border: "1px solid red"
      }}
      className={$className}
    >
      {children}
    </motion.div>
  );
};

export default Parallax; 
